import { SignUp } from "@clerk/clerk-react";
import { Button } from "@growth/ui";
import { LoaderCircle } from "lucide-react";
import { Link } from "react-router-dom";

import { useInvited } from "../lib/invitation";
import { useSignupsOpen } from "../lib/onboarding";

/**
 * Making an account. Open sign-up: anyone, and the welcome flow sets up their workspace.
 * Invite only: just people arriving from an invite email, who go straight into the workspace
 * they were added to. Everyone else is told how to get in.
 */
export const SignUpPage = () => {
  const open = useSignupsOpen();
  const invited = useInvited();

  if (!invited && open === undefined) {
    return (
      <main className="grid min-h-screen place-items-center bg-paper" aria-label="Loading">
        <LoaderCircle className="size-6 animate-spin text-muted" />
      </main>
    );
  }
  if (!invited && !open) return <InviteOnly />;

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-paper px-4 py-10">
      <div className="max-w-sm text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted">Growth Engine</p>
        {invited ? (
          <>
            <h1 className="mt-1 font-serif text-3xl text-ink">You’re invited.</h1>
            <p className="mt-2 text-sm text-muted">
              Make your account and you’ll go straight into the workspace you were added to.
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-1 font-serif text-3xl text-ink">You built it. Now grow it.</h1>
            <p className="mt-2 text-sm text-muted">
              Make an account, tell us about your product, and the agents start reading your site.
            </p>
          </>
        )}
      </div>
      <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" fallbackRedirectUrl="/welcome" />
      <PrivacyNote />
    </main>
  );
};

const InviteOnly = () => (
  <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-paper px-4 py-10">
    <div className="w-full max-w-sm rounded-xl border border-line bg-white p-8 text-center shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-widest text-muted">Growth Engine</p>
      <h1 className="mt-1 font-serif text-2xl text-ink">Invite only for now</h1>
      <p className="mt-2 text-sm text-muted">
        We’re letting people in by invite while we’re early. If someone invited you, open the link
        in their email, or sign in with the address it went to.
      </p>
      <Button asChild className="mt-5 w-full">
        <Link to="/sign-in">Sign in</Link>
      </Button>
    </div>
    <PrivacyNote />
  </main>
);

const PrivacyNote = () => (
  <p className="max-w-sm text-center text-xs text-muted">
    How we handle your account and what the agents read:{" "}
    <Link to="/privacy" className="underline">
      Privacy Policy
    </Link>
    .
  </p>
);
